import type { ReactNode } from 'react';
import { ActionIcon } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useGeoMarkupStore } from '@/store';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { SunIcon, MoonIcon } from '@/components/ui/Icon';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { AppTooltip } from '@/components/ui/AppTooltip';
import { UserProfileMenu } from '@/components/user';
import { useThemeToggle } from '@/hooks';
import { ROUTES } from '@/routes/routes';

/**
 * Minimal top-bar chrome for full-page flows shown outside the AppShell
 * (onboarding, invitation, map configurator): language switcher, theme toggle,
 * and the user avatar menu. Self-contained — reads route + auth internally.
 */
export function OnboardingSettingsBar(): ReactNode {
  const { t } = useTranslation();
  const { colorScheme, handleThemeToggle } = useThemeToggle();
  const location = useLocation();
  const isAuthenticated = useGeoMarkupStore(selectIsAuthenticated);
  const isInvitationPage = location.pathname.startsWith('/invite');
  const isOnboardingPage = location.pathname === ROUTES.ONBOARDING;

  return (
    <>
      <LanguageSelector size="md" />
      <AppTooltip
        labelKey={
          colorScheme === 'dark'
            ? 'tooltips.theme.switchToLight'
            : 'tooltips.theme.switchToDark'
        }
      >
        <ActionIcon
          variant="subtle"
          size="md"
          aria-label={
            colorScheme === 'dark'
              ? t('map.switchToLightTheme')
              : t('map.switchToDarkTheme')
          }
          onClick={handleThemeToggle}
        >
          {colorScheme === 'dark' ? (
            <SunIcon size={20} />
          ) : (
            <MoonIcon size={20} />
          )}
        </ActionIcon>
      </AppTooltip>
      {isAuthenticated && (
        <UserProfileMenu
          size="md"
          hideWorkspaceSelector={isInvitationPage}
          hideSettings={isOnboardingPage}
        />
      )}
    </>
  );
}
